/**
 * Data hooks for the User Tasklist ("Pendências")
 * (`user-tasklist-lexide-handoff-2026-07-20.md`).
 *
 * `useTasklist(filters)` — the list, in the server's canonical order. The
 * client never re-sorts; `groupTasks` only splits into headers. It refetches
 * on the `tasklist.changed` broadcast (any reason, incl. `"inspector"`,
 * is an ordinary refresh trigger) with a poll floor as the fallback the other
 * `*.changed` consumers use.
 *
 * `useTaskDetail(taskId)` — the full record plus a PAGINATED slice of the
 * append-only ledger. The ledger is unbounded, so it is never fetched whole:
 * newest-first pages of `historyLimit`, older rows pulled via `historyBefore`,
 * `historyTotal` sizing the "load older" control.
 *
 * Both read whichever source `lx-tasklist` selects — LIVE since
 * `[M:tasklist-api]` landed 2026-07-21. No hook or component is aware of which.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { useLexEventStream } from "@/hooks/useEventStream";
import {
  getTask,
  listTasks,
  type TaskDetailResult,
  type TaskRow,
  type TasklistFilters,
  type UpdateRow,
} from "@/api/lx-tasklist";

/**
 * Poll cadence — the FALLBACK behind the `plugin.tasklist.changed` broadcast,
 * which is the primary refresh signal.
 *
 * This was tightened to 20 s while the runtime emitted the event through a
 * per-gateway-request broadcaster, so an off-gateway write (the concierge tool,
 * the inspector harness) reached nobody and only the poll could surface it.
 * That defect is fixed (`4ae5e1a6` resolves the broadcaster at emit time) and
 * the runtime lane proved off-gateway delivery with a two-socket test on
 * galvão — so the tight poll is no longer load-bearing and returns to 60 s.
 * Refetch-on-focus/visibilitychange below stays regardless: it is a genuine UX
 * win (returning to the tab is exactly when stale data is most obvious),
 * independent of the broadcast.
 */
const POLL_MS = 60_000;
/** Hidden tab: stop burning requests nobody is looking at. */
const POLL_HIDDEN_MS = 120_000;

export interface UseTasklistResult {
  tasks: TaskRow[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useTasklist(filters: TasklistFilters = {}): UseTasklistResult {
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const disposedRef = useRef(false);

  // Serialize the filters so the fetch identity is stable across renders.
  const key = JSON.stringify(filters);

  const fetchList = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await listTasks(JSON.parse(key) as TasklistFilters);
      if (disposedRef.current) return;
      setTasks(res.tasks ?? []);
      setError(null);
    } catch (err) {
      if (disposedRef.current) return;
      setError(err instanceof Error ? err.message : "tasklist unavailable");
    } finally {
      if (!disposedRef.current) setIsLoading(false);
    }
  }, [key]);

  useEffect(() => {
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
    };
  }, []);

  useEffect(() => {
    void fetchList();
    let timer: ReturnType<typeof setInterval> | undefined;
    const schedule = () => {
      if (timer) clearInterval(timer);
      const hidden =
        typeof document !== "undefined" && document.visibilityState === "hidden";
      timer = setInterval(() => void fetchList(), hidden ? POLL_HIDDEN_MS : POLL_MS);
    };
    schedule();

    // Coming BACK to the tab is the moment stale data is most obvious — and
    // the most likely moment an agent changed something while you were away.
    // Refetch immediately, then re-pace the timer for the new visibility.
    const onVisible = () => {
      if (document.visibilityState === "visible") void fetchList();
      schedule();
    };
    const onFocus = () => void fetchList();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return () => {
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, [fetchList]);

  // Any tasklist mutation — from this SPA, chat, IM, or the inspector —
  // announces itself. `reason` is not branched on (§4.2): every reason,
  // including "inspector", is an ordinary refresh trigger.
  // The bare §4 name — `useLexEventStream` resolves the `plugin.` wire
  // namespace that pristine OpenClaw core requires for delivery (core drops any
  // event lacking an EVENT_SCOPE_GUARDS entry outside that namespace). Keeping
  // the namespace rule in ONE helper rather than hardcoded at each consumer is
  // what stops the next subscriber from re-learning it the hard way.
  useLexEventStream(
    "tasklist.changed",
    useCallback(() => {
      void fetchList();
    }, [fetchList]),
  );

  return { tasks, isLoading, error, refetch: fetchList };
}

export interface UseTaskDetailResult {
  task: TaskRow | null;
  history: UpdateRow[];
  historyTotal: number;
  isLoading: boolean;
  error: string | null;
  /** True while an older page is in flight. */
  isLoadingMore: boolean;
  hasMoreHistory: boolean;
  loadOlder: () => Promise<void>;
  refetch: () => Promise<void>;
}

/** Rows per timeline page. The wire's *server* default is 50 (used when the
 *  client omits `historyLimit`); the UI asks for a smaller window so a long
 *  ledger renders progressively behind "carregar mais antigas" instead of
 *  arriving whole. */
const HISTORY_PAGE = 10;

export function useTaskDetail(
  taskId: string | null,
  /** Bumped by the list's `tasklist.changed` so an open detail refreshes when
   *  its own task mutates. */
  changeToken = 0,
): UseTaskDetailResult {
  const [detail, setDetail] = useState<TaskDetailResult | null>(null);
  const [history, setHistory] = useState<UpdateRow[]>([]);
  const [isLoading, setIsLoading] = useState(Boolean(taskId));
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const disposedRef = useRef(false);

  useEffect(() => {
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
    };
  }, []);

  const fetchFirst = useCallback(async () => {
    if (!taskId) {
      setDetail(null);
      setHistory([]);
      return;
    }
    setIsLoading(true);
    try {
      const res = await getTask(taskId, { historyLimit: HISTORY_PAGE });
      if (disposedRef.current) return;
      setDetail(res);
      setHistory(res.history ?? []);
      setError(null);
    } catch (err) {
      if (disposedRef.current) return;
      setError(err instanceof Error ? err.message : "task unavailable");
    } finally {
      if (!disposedRef.current) setIsLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    void fetchFirst();
  }, [fetchFirst, changeToken]);

  const loadOlder = useCallback(async () => {
    if (!taskId || history.length === 0) return;
    const oldest = history[history.length - 1];
    if (!oldest) return;
    setIsLoadingMore(true);
    try {
      // `historyBefore` is the `at` of the oldest row we already hold, so the
      // server returns the next-older page without re-sending what we have.
      const res = await getTask(taskId, {
        historyLimit: HISTORY_PAGE,
        historyBefore: oldest.at,
      });
      if (disposedRef.current) return;
      setHistory((prev) => [...prev, ...(res.history ?? [])]);
      setDetail((prev) => (prev ? { ...prev, historyTotal: res.historyTotal } : res));
    } finally {
      if (!disposedRef.current) setIsLoadingMore(false);
    }
  }, [taskId, history]);

  return {
    task: detail?.task ?? null,
    history,
    historyTotal: detail?.historyTotal ?? 0,
    isLoading,
    error,
    isLoadingMore,
    hasMoreHistory: history.length < (detail?.historyTotal ?? 0),
    loadOlder,
    refetch: fetchFirst,
  };
}
